import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { ArrowRight, Check, Video } from 'lucide-react';
import { translations } from '../../../utils/language';
import { Button } from '../../../empreenderUI/ui/Button';
import Gradient from '../../../empreenderUI/ui/Gradient';

interface EmptyCreatePageProps {
  title?: string;
  description?: string;
  createButtonText?: string;
  helpButtonText?: string;
  onCreateClick?: () => void;
  onHelpClick?: () => void;
  benefits?: string[];
  showAlert?: boolean;
  showButtons?: boolean;
  alertText?: string;
  alertDetailsText?: string;
  alertDetailsLink?: string;
  gradientColors?: string[];
}

const DEFAULT_BENEFIT_KEYS = ['empty-default-benefit-1', 'empty-default-benefit-2', 'empty-default-benefit-3', 'empty-default-benefit-4', 'empty-default-benefit-5'];

export default function EmptyCreatePage({
  title,
  description = '',
  helpButtonText,
  createButtonText,
  onCreateClick,
  onHelpClick,
  benefits,
  showAlert = false,
  alertText = '',
  alertDetailsText = 'Details',
  alertDetailsLink = '#',
  showButtons = true,
  gradientColors = ['#D2122E', '#FFE600'],
}: EmptyCreatePageProps) {
  const language = useSelector((state: any) => state.reducerLanguage);
  const lang = language?.data || 'pt';
  const titleText = title ?? translations['empty-default-title'][lang];
  const helpText = helpButtonText ?? translations['Como Funciona'][lang];
  const createText = createButtonText ?? translations['medias-cadastrar'][lang];
  const [isVisible, setIsVisible] = useState(false);
  const hasActions = showButtons && (!!onCreateClick || !!onHelpClick);
  const benefitList = benefits?.length ? benefits : DEFAULT_BENEFIT_KEYS.map((key) => translations[key][lang]);

  useEffect(() => {
    const timer = setTimeout(() => setIsVisible(true), 100);
    return () => clearTimeout(timer);
  }, []);

  return (
    <div className='w-full min-h-[calc(100vh-160px)] flex items-center justify-center bg-white text-gray-900 px-4 py-10'>
      <Gradient colors={gradientColors} />

      <div className='relative z-10 max-w-[455px] w-full'>
        {showAlert && (
          <div className={`mb-6 bg-blue-50 p-4 rounded-md flex items-start transform transition-all duration-500 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
            <div className='text-blue-700 mr-3'>
              <svg width='15' height='15' viewBox='0 0 15 15' fill='none' xmlns='http://www.w3.org/2000/svg'>
                <path d='M7.5 1.25C4.05 1.25 1.25 4.05 1.25 7.5C1.25 10.95 4.05 13.75 7.5 13.75C10.95 13.75 13.75 10.95 13.75 7.5C13.75 4.05 10.95 1.25 7.5 1.25ZM7.5 7.5V5M7.5 10H7.51' stroke='#153799' strokeWidth='1.25' />
              </svg>
            </div>
            <div className='flex-1 text-blue-700 text-sm'>{alertText}</div>
            <a href={alertDetailsLink} className='flex items-center text-blue-700 hover:underline'>
              <span className='text-sm font-medium mr-2'>{alertDetailsText}</span>
              <svg width='20' height='20' viewBox='0 0 20 20' fill='none' xmlns='http://www.w3.org/2000/svg'>
                <path d='M2.5 10H17.5M17.5 10L12.5 5M17.5 10L12.5 15' stroke='#1D4ED8' strokeWidth='1.5' />
              </svg>
            </a>
          </div>
        )}

        <div className={`transition-all duration-500 delay-100 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div className='mb-8'>
            <h1 className='text-[32px] font-bold leading-[34px] text-[#0C182E] mb-2'>{titleText}</h1>
            {description ? <p className='text-base leading-7 text-[#3B4457]'>{description}</p> : null}
          </div>

          <div className='mb-8 space-y-3'>
            {benefitList.map((benefit, index) => (
              <div
                key={index}
                className={`flex items-start transform transition-all duration-500 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}
                style={{ transitionDelay: `${150 + index * 50}ms` }}
              >
                <div className='mr-5 mt-0.5 shrink-0'>
                  <Check size={20} color='#22C55E' />
                </div>
                <span className='text-sm text-gray-500'>{benefit}</span>
              </div>
            ))}
          </div>

          {hasActions && (
            <div className={`flex flex-col sm:flex-row gap-3 transform transition-all duration-500 delay-300 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
              {onHelpClick && (
                <Button type='button' variant='outline' className='h-11 px-5' onClick={onHelpClick}>
                  <Video className='size-4' />
                  {helpText}
                </Button>
              )}

              {onCreateClick && (
                <Button type='button' variant='default' className='h-11 px-5' onClick={onCreateClick}>
                  {createText}
                  <ArrowRight className='size-4' />
                </Button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
